import { put, take, race } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import { fromJS } from 'immutable'
import _ from 'lodash'

import { sendMessage } from '../../../../services/socket/helpers'
import { SOCKET_ACTION_TYPE } from '../../../../services/socket/constants'

import { loadGroups } from '../../../../scenes/Gnosus/components/EntityStack/components/StackContent/components/GroupsContent/actions'
import { LOAD_GROUPS_REQUEST } from '../../../../scenes/Gnosus/components/EntityStack/components/StackContent/components/GroupsContent/constants'

import { importGroupMembersRequest, manageGroupsDialogSetRows, manageGroupsGetGroupsForEntityRequest } from './actions'

import { IMPORT_GROUP_MEMBERS_REQUEST, MANAGE_GROUPS_GET_GROUPS_FOR_ENTITY_REQUEST } from './constants'

const LOAD_GROUPS_REQUEST_SUCCESS = `${LOAD_GROUPS_REQUEST}_${SUCCESS}`
const LOAD_GROUPS_REQUEST_FAILED = `${LOAD_GROUPS_REQUEST}_${FAILED}`

const IMPORT_GROUP_MEMBERS_REQUEST_SUCCESS = (country, index) =>
    `${IMPORT_GROUP_MEMBERS_REQUEST(country, index)}_${SUCCESS}`
const IMPORT_GROUP_MEMBERS_REQUEST_FAILED = (country, index) =>
    `${IMPORT_GROUP_MEMBERS_REQUEST(country, index)}_${FAILED}`

const MANAGE_GROUPS_GET_GROUPS_FOR_ENTITY_REQUEST_SUCCESS = `${MANAGE_GROUPS_GET_GROUPS_FOR_ENTITY_REQUEST}_${SUCCESS}`
const MANAGE_GROUPS_GET_GROUPS_FOR_ENTITY_REQUEST_FAILED = `${MANAGE_GROUPS_GET_GROUPS_FOR_ENTITY_REQUEST}_${FAILED}`

/**
 * Returns an array of entity registrations numbers (or ids for natural persons) filtered by country
 * @param {array} entities Array of entities
 * @param {string} country Country in ISO2 format
 * @returns Array of entity regNbrs or ids
 */
export const getEntIdsByCountry = (entities, country) =>
    _(entities)
        .filter((e) => e.get('country').get('iso2') === country) // Filter entities by country
        .map((e) => {
            // Get registration numbers (ICO) or entity id
            const type = e.get('type')

            if (type === 'naturalPerson') return e.get('id', null)

            return e.get(type).get('regNbr', null)
        })
        .value() // Lodash unwrap

/**
 * Combines response objects from import group members request into one object which is then used in the success toast
 * @param  {...any} objs Response objects from import group members request (see importMembersCall function)
 * @returns Returns response object with combined values
 */
export const getResponseObject = (...objs) => {
    const output = {
        ok: 0,
        notFound: 0,
        duplicated: 0,
        exists: 0,
        total: 0,
    }

    _.map(objs, (obj) => {
        const objKeys = Object.keys(obj)

        _.map(objKeys, (key) => {
            if (_.has(output, key) && _.isFinite(_.get(obj, key))) {
                output[key] += obj[key]
            }
        })
    })

    return output
}

/**
 * Helper function for importMultipleSaga
 * Creates a request for members import and returns the response object
 */
export function* importMembersCall(entities, groupId, country, index) {
    yield put(importGroupMembersRequest(entities, groupId, country, index))

    return yield race({
        success: take(IMPORT_GROUP_MEMBERS_REQUEST_SUCCESS(country, index)),
        failed: take(IMPORT_GROUP_MEMBERS_REQUEST_FAILED(country, index)),
    })
}

/**
 * Helper function for import single and remove sagas
 * Create a request for groups in which an entity is present
 * Returns array of objects (groups)
 */
export function* getIsPresentInGroups(entId) {
    yield put(manageGroupsGetGroupsForEntityRequest(entId))

    const result = yield race({
        success: take(MANAGE_GROUPS_GET_GROUPS_FOR_ENTITY_REQUEST_SUCCESS),
        failed: take(MANAGE_GROUPS_GET_GROUPS_FOR_ENTITY_REQUEST_FAILED),
    })

    return fromJS(_.get(result, 'success.payload.response.body', []))
}

/**
 * Helper function for sagas
 * Creates a request for groups (which also realods group stack - i.e. member counts)
 * Saves rows to state for dialog by groups and by in which groups an entity is present
 */
export function* reloadRows(isPresentInGroups) {
    yield put(loadGroups())

    yield sendMessage(SOCKET_ACTION_TYPE.REFRESH_GROUPS, { type: 'REFRESH', groupId: null })

    const result = yield race({
        success: take(LOAD_GROUPS_REQUEST_SUCCESS),
        failed: take(LOAD_GROUPS_REQUEST_FAILED),
    })

    if (result.success) {
        const groups = fromJS(_.get(result, 'success.payload.response.body', []))

        yield put(manageGroupsDialogSetRows(groups, isPresentInGroups))
    }

    return null
}
